import React, { useEffect, useState } from "react";
import { Target as TargetIcon, Plus, Trash2, TrendingUp, Calendar } from "lucide-react";
import { toast } from "sonner";

import { v2Api } from "@/lib/v2Api";
import { confirm } from "@/lib/confirm";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const PERIOD_KINDS = ["monthly", "quarterly", "yearly"];
const TARGET_KINDS = [
  { value: "won_value", label: "Won value (₹)", isMoney: true },
  { value: "won_count", label: "Won count" },
  { value: "submission_count", label: "Submissions" },
  { value: "pipeline_value", label: "Pipeline value (₹)", isMoney: true },
];

const todayIso = () => new Date().toISOString().slice(0, 10);

function defaultRangeFor(kind) {
  const now = new Date();
  if (kind === "monthly") {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return [start.toISOString().slice(0,10), end.toISOString().slice(0,10)];
  }
  if (kind === "quarterly") {
    const q = Math.floor(now.getMonth() / 3);
    const start = new Date(now.getFullYear(), q * 3, 1);
    const end = new Date(now.getFullYear(), q * 3 + 3, 0);
    return [start.toISOString().slice(0,10), end.toISOString().slice(0,10)];
  }
  return [`${now.getFullYear()}-04-01`, `${now.getFullYear() + 1}-03-31`]; // FY (Apr–Mar)
}

function NewTargetDialog({ open, onOpenChange, users, departments, products, onSaved }) {
  const blank = () => {
    const [s, e] = defaultRangeFor("monthly");
    return {
      period_kind: "monthly", period_start: s, period_end: e,
      target_kind: "won_value", target_value: "",
      assignee_user_id: "", department_id: "", product_id: "", note: "",
    };
  };
  const [form, setForm] = useState(blank());
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm(blank()); }, [open]);

  const setKind = (kind) => {
    const [s, e] = defaultRangeFor(kind);
    setForm((f) => ({ ...f, period_kind: kind, period_start: s, period_end: e }));
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.target_value || Number(form.target_value) <= 0) {
      toast.error("Set a positive target"); return;
    }
    setBusy(true);
    try {
      await v2Api.targets.create({
        period_kind: form.period_kind,
        period_start: form.period_start,
        period_end: form.period_end,
        target_kind: form.target_kind,
        target_value: Number(form.target_value),
        assignee_user_id: form.assignee_user_id || null,
        department_id: form.department_id || null,
        product_id: form.product_id || null,
        note: form.note.trim() || null,
      });
      toast.success("Target added");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>New target</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2 text-sm">
          <div className="grid grid-cols-3 gap-2">
            {PERIOD_KINDS.map((k) => (
              <button key={k} onClick={() => setKind(k)}
                      className={`text-xs px-2 py-1 rounded-full border ${
                        form.period_kind === k
                          ? "bg-blue-600 text-white border-blue-600"
                          : "bg-white text-slate-600 border-slate-200"
                      }`}>
                {k}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-slate-500">Period start</Label>
              <Input type="date" value={form.period_start} onChange={set("period_start")} />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Period end</Label>
              <Input type="date" value={form.period_end} onChange={set("period_end")} />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Target type</Label>
            <select value={form.target_kind} onChange={set("target_kind")}
                    className="w-full px-2 py-1 border rounded-md bg-white text-sm">
              {TARGET_KINDS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Target value</Label>
            <Input type="number" value={form.target_value} onChange={set("target_value")}
                   placeholder={TARGET_KINDS.find((t) => t.value === form.target_kind)?.isMoney ? "e.g. 50000000 (5 Cr)" : "e.g. 12"} />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Assignee (optional)</Label>
            <select value={form.assignee_user_id} onChange={set("assignee_user_id")}
                    className="w-full px-2 py-1 border rounded-md bg-white text-sm">
              <option value="">— all users —</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.full_name || u.name || u.email}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Department (optional)</Label>
            <select value={form.department_id} onChange={set("department_id")}
                    className="w-full px-2 py-1 border rounded-md bg-white text-sm">
              <option value="">— all departments —</option>
              {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Product (optional)</Label>
            <select value={form.product_id} onChange={set("product_id")}
                    className="w-full px-2 py-1 border rounded-md bg-white text-sm">
              <option value="">— all products —</option>
              {products.map((p) => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Note</Label>
            <Input value={form.note} onChange={set("note")} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy}>{busy ? "Saving…" : "Add target"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProgressBar({ pct }) {
  const cls = pct >= 100 ? "bg-emerald-500"
            : pct >= 70 ? "bg-blue-500"
            : pct >= 40 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div className="bg-slate-100 rounded h-2 relative overflow-hidden">
      <div className={`h-2 rounded ${cls}`} style={{ width: `${Math.min(150, pct)}%` }} />
    </div>
  );
}

export default function TargetsPage() {
  const [targets, setTargets] = useState([]);
  const [users, setUsers] = useState([]);
  const [depts, setDepts] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [t, u, d, p] = await Promise.all([
        v2Api.targets.list(true),
        v2Api.users(),
        v2Api.departments(),
        v2Api.catalog.products(),
      ]);
      setTargets(t || []);
      setUsers(u || []);
      setDepts(d || []);
      setProducts(p || []);
    } catch { toast.error("Could not load targets"); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const remove = async (t) => {
    if (!await confirm("Delete this target?", { title: "Delete target", confirmLabel: "Delete" })) return;
    try { await v2Api.targets.remove(t.id); refresh(); }
    catch { toast.error("Delete failed"); }
  };

  const fmtVal = (n, kind) => {
    const isMoney = kind === "won_value" || kind === "pipeline_value";
    if (n == null) return "—";
    if (!isMoney) return new Intl.NumberFormat("en-IN").format(n);
    if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
    if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
    return `₹${new Intl.NumberFormat("en-IN").format(n)}`;
  };
  const labelFor = (id, list, key = "name") => list.find((x) => x.id === id)?.[key] || "—";

  return (
    <div className="p-8 max-w-5xl mx-auto space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <TargetIcon className="w-5 h-5 text-blue-600" /> Targets
          </h1>
          <p className="text-sm text-slate-500">
            Monthly / quarterly / yearly goals with auto-computed actuals.
            Scope: workspace, department, product, or per-user.
          </p>
        </div>
        <Button onClick={() => setOpen(true)}>
          <Plus className="w-4 h-4 mr-1" /> Add target
        </Button>
      </div>

      {loading && <p className="text-sm text-slate-500">Loading…</p>}

      {!loading && targets.length === 0 && (
        <Card><CardContent className="py-10 text-center text-slate-400">
          No targets yet. Add one to start tracking pipeline / win value /
          submission count against goals.
        </CardContent></Card>
      )}

      <div className="space-y-2">
        {targets.map((t) => (
          <Card key={t.id} className={t.completion_pct >= 100 ? "border-emerald-200" : ""}>
            <CardContent className="py-3 space-y-1.5">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center flex-wrap gap-2">
                    <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-[10px]">{t.period_kind}</Badge>
                    <Badge variant="outline" className="text-[10px]">{t.target_kind}</Badge>
                    <span className="text-xs text-slate-500 font-mono flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> {t.period_start} → {t.period_end}
                    </span>
                  </div>
                  <p className="text-sm text-slate-700 mt-1">
                    {t.assignee_user_id && <>👤 {labelFor(t.assignee_user_id, users, "full_name") || labelFor(t.assignee_user_id, users)} · </>}
                    {t.department_id && <>🏢 {labelFor(t.department_id, depts)} · </>}
                    {t.product_id && <>📦 {labelFor(t.product_id, products)} · </>}
                    {!t.assignee_user_id && !t.department_id && !t.product_id && <em className="text-slate-400">workspace-wide</em>}
                  </p>
                  {t.note && <p className="text-xs text-slate-500">{t.note}</p>}
                </div>
                <div className="text-right shrink-0">
                  <p className={`text-xl font-semibold ${
                    t.completion_pct >= 100 ? "text-emerald-700" :
                    t.completion_pct >= 70 ? "text-blue-700" :
                    t.completion_pct >= 40 ? "text-amber-700" : "text-rose-700"
                  }`}>
                    {t.completion_pct.toFixed(0)}%
                  </p>
                  <p className="text-[10px] text-slate-500">
                    {fmtVal(t.actual_value, t.target_kind)} / {fmtVal(t.target_value, t.target_kind)}
                  </p>
                  <button onClick={() => remove(t)} className="text-slate-300 hover:text-rose-500 mt-1">
                    <Trash2 className="w-3.5 h-3.5 inline" />
                  </button>
                </div>
              </div>
              <ProgressBar pct={t.completion_pct} />
            </CardContent>
          </Card>
        ))}
      </div>

      <NewTargetDialog
        open={open} onOpenChange={setOpen}
        users={users} departments={depts} products={products}
        onSaved={refresh}
      />
    </div>
  );
}
